import { Slider } from "@heroui/react";
import { useTheme } from "next-themes";

import { usePanel } from "@/contexts/PanelContext";

import { useAppContext } from "../../contexts/AppContext";
import { useDialog } from "../../contexts/DialogContext";
import { useIsCompactPaletteDefault } from "../../hooks/useCompactPalette";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Button } from "../core/Button";
import { Switch } from "../core/Switch";

export const MiscSettings = () => {
  const { config, settings } = useAppContext();
  const Dialog = useDialog();
  const Panel = usePanel();
  const { resolvedTheme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  const compactPaletteDefault = useIsCompactPaletteDefault();
  const compactPaletteSetting = settings.get["palette.compact"];
  const compactPaletteSelected = compactPaletteSetting ?? compactPaletteDefault;
  const showCompactPaletteSettings = !isMobile;

  return (
    <div className="flex flex-col gap-4 p-2">
      <header className="flex flex-col gap-2">
        <h2 className="text-xl">Misc</h2>
        <p className="text-xs text-default-600">Other settings</p>
      </header>
      <section className="flex gap-2 flex-col">
        <Switch
          isSelected={resolvedTheme === "dark"}
          onValueChange={(value) => {
            setTheme(value ? "dark" : "light");
          }}
        >
          Dark Mode
        </Switch>
        <Switch
          isSelected={settings.get["capture.cameraFlash"]}
          onValueChange={(value) => {
            settings.set({ key: "capture.cameraFlash", value });
          }}
        >
          Enable Camera Flash
        </Switch>
        {!isMobile && config?.cursors.enabled && (
          <>
            <Switch
              isSelected={settings.get["presence.cursors"]}
              onValueChange={(value) => {
                settings.set({ key: "presence.cursors", value });
              }}
            >
              Enable Cursors
            </Switch>
            {settings.get["presence.cursors"] && (
              <Switch
                isSelected={settings.get["presence.reactions"]}
                onValueChange={(value) => {
                  settings.set({ key: "presence.reactions", value });
                }}
              >
                Enable Reactions
              </Switch>
            )}
          </>
        )}
        <Switch
          isSelected={settings.get["keybindings.enabled"]}
          onValueChange={(value) => {
            settings.set({ key: "keybindings.enabled", value });
          }}
        >
          Enable Keybindings
        </Switch>
        <Switch
          isSelected={settings.get["palette.colors"]}
          onValueChange={(value) => {
            settings.set({ key: "palette.colors", value });
          }}
        >
          Show Palette Numbers
        </Switch>
        <Switch
          isSelected={settings.get["palette.symbols"]}
          onValueChange={(value) => {
            settings.set({ key: "palette.symbols", value });
          }}
        >
          Show Palette Symbols
        </Switch>
        <Switch
          isSelected={settings.get["ui.compactButtons"]}
          onValueChange={(value) => {
            settings.set({ key: "ui.compactButtons", value });
          }}
        >
          Compact Buttons
        </Switch>
        {!settings.get["ui.compactButtons"] && (
          <Switch
            className="sidebar-adaptation-setting"
            isSelected={settings.get["ui.compactThemeSwitcher"]}
            onValueChange={(value) => {
              settings.set({ key: "ui.compactThemeSwitcher", value });
            }}
          >
            Compact Theme Switcher
          </Switch>
        )}
        <Switch
          isSelected={settings.get["ui.showThemeSwitcher"]}
          onValueChange={(value) => {
            settings.set({ key: "ui.showThemeSwitcher", value });
          }}
        >
          Show Theme Switcher
        </Switch>
        {showCompactPaletteSettings && (
          <Switch
            isSelected={compactPaletteSelected}
            onValueChange={(value) => {
              settings.set({ key: "palette.compact", value });
            }}
          >
            Compact Palette
          </Switch>
        )}
        {showCompactPaletteSettings &&
          compactPaletteSetting === undefined &&
          !compactPaletteDefault && (
            <Switch
              className="sidebar-adaptation-setting"
              isSelected={settings.get["ui.compactPaletteForSidebar"]}
              onValueChange={(value) => {
                settings.set({ key: "ui.compactPaletteForSidebar", value });
              }}
            >
              Adapt Palette When Sidebar is Open
            </Switch>
          )}
        <Switch
          className="sidebar-adaptation-setting"
          isSelected={settings.get["ui.adaptElementsToSidebar"]}
          onValueChange={(value) => {
            settings.set({ key: "ui.adaptElementsToSidebar", value });
          }}
        >
          Adapt UI Elements When Sidebar is Open
        </Switch>
        <Switch
          className="show-overlays-card-setting"
          isSelected={settings.get["ui.showOverlaysCard"]}
          onValueChange={(value) => {
            settings.set({ key: "ui.showOverlaysCard", value });
          }}
        >
          Show Overlays Card
        </Switch>
        <Slider
          className="px-2"
          label="Zoom Sensitivity"
          minValue={0.25}
          maxValue={2}
          step={0.05}
          value={settings.get["canvas.zoomSensitivity"]}
          onChange={(value) => {
            settings.set({
              key: "canvas.zoomSensitivity",
              value: value as number,
            });
          }}
          getValue={(value) => Math.round((value as number) * 100) + "%"}
        />
        <Button
          className="w-fit"
          onPress={() => {
            Dialog.dispatch({
              action: "OPEN_DIALOG",
              dialog: { id: "KEYBINDS" },
            });
            Panel.dispatch(["settings", undefined]);
          }}
        >
          Keybinds
        </Button>
        <Button
          className="w-fit"
          onPress={() => {
            // Dialog.dispatch({ action: "OPEN_DIALOG", dialog: { id: "WELCOME" } });
            Dialog.dispatch({
              action: "OPEN_DIALOG",
              dialog: { id: "EVENT_INFO" },
            });
            Panel.dispatch(["settings", undefined]);
          }}
        >
          Open Welcome Message
        </Button>
      </section>
    </div>
  );
};
